import { useMemo, useState } from 'react'
import { times } from 'lodash'
import { DateTime } from 'luxon'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  Button,
  Card,
  CardColors,
  Checkbox,
  Dropdown,
  Icon,
  Input,
  Link,
  Popup,
  Select,
  Table,
  Text,
} from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import {
  fetchUserIncomeMonthsForYear,
  selectIncomePlanningMonthsForYear,
  UserIncomePlanMonth,
} from '@/features/income-planning/incomePlanning.slice'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { IncomeMonthSheet } from '@/features/income-planning/income-planning-months/IncomeMonthSheet'
import { ApplyPlanDialog } from '@/features/income-planning/income-planning-months/ApplyPlanDialog'

export const IncomePlanningMonths = () => {
  const fp = useReselector(getFinancialProfile)

  const currentDate = useMemo(() => DateTime.now(), [])
  const [selectedYear, setSelectedYear] = useState(currentDate.year)
  const [monthToShow, setMonthToShow] = useState<UserIncomePlanMonth | null>(
    null
  )
  const [selectedMonths, setSelectedMonths] = useState<number[]>([])

  const incomePlaningMonths = useReselector(
    selectIncomePlanningMonthsForYear,
    selectedYear
  )

  const incomePlaningMonthsRes = useFetchResponse(
    fetchUserIncomeMonthsForYear,
    selectedYear
  )

  return (
    <>
      <Text as={TypographyStyles.DISPLAY_XS}>Income Planning</Text>
      <div className="mt-10 w-25">
        <Select
          value={selectedYear}
          onValueChange={setSelectedYear}
          options={[
            { text: currentDate.year.toString(), value: currentDate.year },
            {
              text: (currentDate.year + 1).toString(),
              value: currentDate.year + 1,
            },
          ]}
        />
      </div>
      <div className="flex gap-4 mt-4">
        <Card className="w-1/3" color={CardColors.LEVEL_1}>
          <Icon icon={light('calendar-clock')} backgroundClassName="w-6" />
          <Text variant={TypographyStyles.DISPLAY_MD} className="mt-4">
            {incomePlaningMonthsRes?.pastYearTotalBusinessIncomeInCents
              ? formatCurrencyFromCents(
                  incomePlaningMonthsRes.pastYearTotalBusinessIncomeInCents
                )
              : '-'}
          </Text>
          <Popup
            content={
              <>
                <Text variant={TypographyStyles.LABEL_MD}>
                  {currentDate.year - 1} Income
                </Text>
                <Text variant={TypographyStyles.BODY_SM} className="mt-2">
                  This is the total income that has been categorized by
                  Heard&apos;s bookkeeping team for {currentDate.year - 1}.
                </Text>
              </>
            }
            trigger={
              <Link variant="secondary" className="mt" href="">
                {currentDate.year - 1} Income
              </Link>
            }
          />
          <br />
          <Link href="/insights/income" className="mt-4">
            View income breakdown →
          </Link>
        </Card>
        <Card className="w-1/3" color={CardColors.LEVEL_1}>
          <Icon icon={light('calculator')} backgroundClassName="w-6" />
          <Text variant={TypographyStyles.DISPLAY_MD} className="mt-4">
            {fp?.annualIncomeGoalInCents
              ? formatCurrencyFromCents(fp.annualIncomeGoalInCents)
              : '-'}
          </Text>
          <Popup
            content={
              <>
                <Text variant={TypographyStyles.LABEL_MD}>Planned Income</Text>
                <Text variant={TypographyStyles.BODY_SM} className="mt-2">
                  This is your forecasted monthly earnings, which you&apos;ve
                  entered either by applying a plan or by inputting it manually.
                </Text>
              </>
            }
            trigger={
              <Link variant="secondary" className="mt" href="">
                {currentDate.year} Planned Income
              </Link>
            }
          />
          <br />
          <Link href="/goal-setting" className="mt-4">
            View annual goals →
          </Link>
        </Card>
        <Card className="w-1/3" color={CardColors.LEVEL_1}>
          <Icon icon={light('money-check-dollar')} backgroundClassName="w-6" />
          <Text variant={TypographyStyles.DISPLAY_MD} className="mt-4">
            {incomePlaningMonthsRes?.currentYearTotalBusinessIncomeInCents
              ? formatCurrencyFromCents(
                  incomePlaningMonthsRes.currentYearTotalBusinessIncomeInCents
                )
              : '-'}
          </Text>
          <Popup
            content={
              <>
                <Text variant={TypographyStyles.LABEL_MD}>
                  {currentDate.year} Year-to-date Income
                </Text>
                <Text variant={TypographyStyles.BODY_SM} className="mt-2">
                  This is the total income that has been categorized by
                  Heard&apos;s bookkeeping team so far this year. The percentage
                  increase or decrease is your actual year-to-date income as
                  compared to your planned income.
                  <br />
                  Note: This amount may not exactly align with your planned
                  income, as reimbursement times from insurance plans vary.
                </Text>
              </>
            }
            trigger={
              <Link variant="secondary" className="mt" href="">
                {currentDate.year} Year-to-date Income
              </Link>
            }
          />
          <br />
          {fp?.annualIncomeGoalInCents &&
            incomePlaningMonthsRes?.currentYearTotalBusinessIncomeInCents && (
              <Text variant={TypographyStyles.BODY_SM} className="mt-4">
                <b>
                  {(
                    ((fp.annualIncomeGoalInCents -
                      incomePlaningMonthsRes.currentYearTotalBusinessIncomeInCents) /
                      fp.annualIncomeGoalInCents) *
                    100
                  ).toFixed(2)}
                  %
                </b>{' '}
                to income goal of{' '}
                <b>{formatCurrencyFromCents(fp.annualIncomeGoalInCents)}</b>
              </Text>
            )}
        </Card>
      </div>
      <div className="flex justify-between mt-10">
        <ApplyPlanDialog
          selectedMonths={selectedMonths}
          selectedYear={selectedYear}
          setSelectedMonths={setSelectedMonths}
        />
        <Dropdown>
          <Dropdown.Trigger>
            <Button iconLeft={light('plus')}>Create Plan</Button>
          </Dropdown.Trigger>
          <Dropdown.Content>
            <Dropdown.Item>Create new template</Dropdown.Item>
            <Dropdown.Item>Create from existing template</Dropdown.Item>
            <Dropdown.Item>Edit existing template</Dropdown.Item>
          </Dropdown.Content>
        </Dropdown>
      </div>
      <Table className="mt-4">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              <Checkbox
                checked={selectedMonths.length === 12}
                onCheckedChange={() =>
                  setSelectedMonths(
                    selectedMonths.length === 12
                      ? []
                      : times(12, (val) => val + 1)
                  )
                }
              />
            </Table.HeaderCell>
            <Table.HeaderCell>
              <Text variant={TypographyStyles.EYEBROW}>Date</Text>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <Text variant={TypographyStyles.EYEBROW}>
                {selectedYear - 1} Income
              </Text>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <Text variant={TypographyStyles.EYEBROW}>Planned Income</Text>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <Text variant={TypographyStyles.EYEBROW}>
                {selectedYear} Income
              </Text>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <Text variant={TypographyStyles.EYEBROW}>Notes</Text>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        {incomePlaningMonths.map((planningMonth) => {
          const percentDifference =
            planningMonth.pastTotalBusinessIncomeInCents &&
            planningMonth.currentTotalBusinessIncomeInCents
              ? ((planningMonth.currentTotalBusinessIncomeInCents -
                  planningMonth.pastTotalBusinessIncomeInCents) /
                  planningMonth.pastTotalBusinessIncomeInCents) *
                100
              : 0

          return (
            <Table.Row key={planningMonth.month}>
              <Table.Cell>
                <Checkbox
                  checked={selectedMonths.includes(planningMonth.month)}
                  onCheckedChange={(val) => {
                    if (val) {
                      setSelectedMonths([
                        ...selectedMonths,
                        planningMonth.month,
                      ])
                    } else {
                      setSelectedMonths(
                        selectedMonths.filter(
                          (month) => month !== planningMonth.month
                        )
                      )
                    }
                  }}
                />
              </Table.Cell>
              <Table.Cell>
                <Button
                  variant="link"
                  onClick={() => setMonthToShow(planningMonth)}
                >
                  {DateTime.fromObject({ month: planningMonth.month }).toFormat(
                    'LLLL'
                  )}
                </Button>
              </Table.Cell>
              <Table.Cell>
                <Text variant={TypographyStyles.BODY_SM}>
                  {planningMonth.pastTotalBusinessIncomeInCents
                    ? formatCurrencyFromCents(
                        planningMonth.pastTotalBusinessIncomeInCents
                      )
                    : '-'}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Input value={planningMonth.totalPlannedIncomeInCents} />
              </Table.Cell>
              <Table.Cell>
                <div className="flex">
                  <Text variant={TypographyStyles.BODY_SM} className="mr-4">
                    {planningMonth.currentTotalBusinessIncomeInCents
                      ? formatCurrencyFromCents(
                          planningMonth.currentTotalBusinessIncomeInCents
                        )
                      : '-'}
                  </Text>

                  {Boolean(percentDifference) && (
                    <>
                      <Icon
                        icon={
                          percentDifference < 0
                            ? light('arrow-down')
                            : light('arrow-up')
                        }
                        color={
                          percentDifference < 0
                            ? 'text-red-200'
                            : 'text-green-100'
                        }
                      />
                      <Text
                        variant={TypographyStyles.LABEL_SM}
                        color={
                          percentDifference < 0
                            ? TextColors.RED
                            : TextColors.GREEN
                        }
                      >
                        {Math.abs(percentDifference).toFixed(1)}%
                      </Text>
                    </>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <Text variant={TypographyStyles.BODY_SM}>
                  {planningMonth.notes || '-'}
                </Text>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table>
      <IncomeMonthSheet
        monthToShow={monthToShow}
        closeSheet={() => setMonthToShow(null)}
      />
    </>
  )
}
